import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  Grid3X3,
  List,
  Trophy,
  Calendar,
  User,
  ExternalLink,
  Star,
  Download,
  Share2,
  Edit3,
  Trash2,
  Award,
  BookOpen,
  Briefcase,
  Heart,
  Camera,
  Code,
  Palette,
  Music,
  Zap
} from 'lucide-react';

interface PortfolioItem {
  id: string;
  title: string;
  category: 'academic' | 'extracurricular' | 'creative' | 'leadership' | 'service' | 'research' | 'internship' | 'project';
  description: string;
  date: string;
  status: 'completed' | 'in-progress' | 'planned';
  images: string[];
  tags: string[];
  achievements: string[];
  reflection: string;
  skills: string[];
  impact: string;
  featured: boolean;
}

interface Achievement {
  id: string;
  title: string;
  category: string;
  date: string;
  description: string;
  icon: string;
  level: 'bronze' | 'silver' | 'gold' | 'platinum';
}

const mockPortfolioItems: PortfolioItem[] = [
  {
    id: '1',
    title: 'Student Council President Campaign',
    category: 'leadership',
    description: 'Led a comprehensive campaign for student council president, implementing innovative digital outreach strategies and organizing school-wide forums.',
    date: '2024-09-01',
    status: 'completed',
    images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
    tags: ['Leadership', 'Public Speaking', 'Campaign Strategy', 'Digital Marketing'],
    achievements: ['Won election with 65% of vote', 'Increased voter turnout by 40%', 'Implemented 3 new school policies'],
    reflection: 'This experience taught me the importance of listening to diverse perspectives and building consensus. I learned to communicate complex ideas simply and to lead with empathy.',
    skills: ['Public Speaking', 'Strategic Planning', 'Team Leadership', 'Digital Marketing'],
    impact: 'Improved school lunch program, established peer tutoring network, and increased student engagement in school governance.',
    featured: true
  },
  {
    id: '2',
    title: 'AI-Powered Study Assistant App',
    category: 'project',
    description: 'Developed a mobile application using React Native and Python that helps students organize study schedules and provides personalized learning recommendations.',
    date: '2024-08-15',
    status: 'completed',
    images: ['/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300'],
    tags: ['Programming', 'AI', 'Mobile Development', 'User Experience'],
    achievements: ['50+ beta users', 'Featured in school tech showcase', 'Open sourced on GitHub'],
    reflection: 'Building this app challenged me to think about how technology can solve real educational problems. I learned full-stack development and gained insights into user-centered design.',
    skills: ['React Native', 'Python', 'Machine Learning', 'UI/UX Design', 'Project Management'],
    impact: 'Helped 50+ students improve their study efficiency by 25% on average.',
    featured: true
  },
  {
    id: '3',
    title: 'Community Garden Initiative',
    category: 'service',
    description: 'Organized and led the establishment of a community garden in an underserved neighborhood, coordinating with local officials and volunteers.',
    date: '2024-07-01',
    status: 'in-progress',
    images: ['/api/placeholder/400/300'],
    tags: ['Environmental Action', 'Community Service', 'Project Management', 'Sustainability'],
    achievements: ['Secured $2,000 in funding', '25 regular volunteers', '200+ families served'],
    reflection: 'This project taught me about environmental justice and the power of grassroots organizing. I learned to navigate bureaucracy and build community partnerships.',
    skills: ['Project Management', 'Grant Writing', 'Community Organizing', 'Environmental Science'],
    impact: 'Provided fresh produce access to 200+ families and created educational opportunities for local children.',
    featured: false
  },
  {
    id: '4',
    title: 'Historical Research on Local Immigration',
    category: 'research',
    description: 'Conducted independent research on immigration patterns in our city from 1900-1950, including interviews with elderly community members and archival research.',
    date: '2024-06-01',
    status: 'completed',
    images: ['/api/placeholder/400/300'],
    tags: ['Historical Research', 'Interview Skills', 'Data Analysis', 'Writing'],
    achievements: ['Published in school journal', 'Presented at regional history fair', 'Received research award'],
    reflection: 'This research deepened my understanding of how historical events affect real families and communities. I developed strong research and analytical skills.',
    skills: ['Research Methods', 'Interview Techniques', 'Data Analysis', 'Academic Writing'],
    impact: 'Preserved important oral histories and contributed to local historical society archives.',
    featured: true
  },
  {
    id: '5',
    title: 'Youth Orchestra First Chair',
    category: 'creative',
    description: 'Achieved first chair violin position in regional youth orchestra and performed in multiple concerts and competitions.',
    date: '2024-05-01',
    status: 'completed',
    images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
    tags: ['Music', 'Performance', 'Discipline', 'Teamwork'],
    achievements: ['First chair for 2 years', 'Solo performance at spring concert', 'Competition finalist'],
    reflection: 'Music has taught me discipline, patience, and the beauty of collaborative creation. Performing has helped me overcome stage fright and express myself authentically.',
    skills: ['Musical Performance', 'Discipline', 'Teamwork', 'Stage Presence'],
    impact: 'Inspired younger students to join orchestra and mentored 5 new violin students.',
    featured: false
  }
];

const mockAchievements: Achievement[] = [
  {
    id: '1',
    title: 'Outstanding Leadership',
    category: 'Leadership',
    date: '2024-09-15',
    description: 'Recognized for exceptional leadership in student government',
    icon: 'trophy',
    level: 'gold'
  },
  {
    id: '2',
    title: 'Innovation Award',
    category: 'Technology',
    date: '2024-08-20',
    description: 'Awarded for innovative mobile app development',
    icon: 'star',
    level: 'silver'
  },
  {
    id: '3',
    title: 'Community Champion',
    category: 'Service',
    date: '2024-07-10',
    description: 'Recognized for outstanding community service',
    icon: 'heart',
    level: 'gold'
  },
  {
    id: '4',
    title: 'Research Scholar',
    category: 'Academic',
    date: '2024-06-15',
    description: 'Honored for exceptional historical research',
    icon: 'book-open',
    level: 'silver'
  }
];

const categoryIcons = {
  academic: BookOpen,
  extracurricular: Trophy,
  creative: Palette,
  leadership: User,
  service: Heart,
  research: BookOpen,
  internship: Briefcase,
  project: Code
};

const categoryColors = {
  academic: 'bg-blue-100 text-blue-800',
  extracurricular: 'bg-green-100 text-green-800',
  creative: 'bg-purple-100 text-purple-800',
  leadership: 'bg-orange-100 text-orange-800',
  service: 'bg-pink-100 text-pink-800',
  research: 'bg-indigo-100 text-indigo-800',
  internship: 'bg-teal-100 text-teal-800',
  project: 'bg-gray-100 text-gray-800'
};

const levelColors = {
  bronze: 'text-orange-600',
  silver: 'text-gray-500',
  gold: 'text-yellow-500',
  platinum: 'text-purple-600'
};

export const Portfolio: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState<'portfolio' | 'achievements' | 'skills'>('portfolio');

  const categories = ['all', 'academic', 'extracurricular', 'creative', 'leadership', 'service', 'research', 'internship', 'project'];

  const filteredItems = mockPortfolioItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredItems = mockPortfolioItems.filter(item => item.featured);

  const skillsData = [
    { category: 'Technical', skills: ['JavaScript', 'Python', 'React', 'Machine Learning', 'Data Analysis'], level: 85 },
    { category: 'Leadership', skills: ['Team Management', 'Public Speaking', 'Strategic Planning', 'Conflict Resolution'], level: 90 },
    { category: 'Creative', skills: ['UI/UX Design', 'Photography', 'Music Performance', 'Creative Writing'], level: 75 },
    { category: 'Research', skills: ['Academic Writing', 'Data Collection', 'Statistical Analysis', 'Literature Review'], level: 80 },
    { category: 'Communication', skills: ['Presentation', 'Interview', 'Networking', 'Cross-cultural Communication'], level: 88 }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Portfolio</h1>
            <p className="text-gray-600">Showcase your achievements, projects, and growth journey</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </motion.button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
          {[
            { key: 'portfolio', label: 'Portfolio Items', icon: Briefcase },
            { key: 'achievements', label: 'Achievements', icon: Award },
            { key: 'skills', label: 'Skills Overview', icon: Zap }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === key
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </motion.div>

      {activeTab === 'portfolio' && (
        <>
          {/* Featured Section */}
          {featuredItems.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-500" />
                Featured Work
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {featuredItems.slice(0, 2).map((item) => {
                  const IconComponent = categoryIcons[item.category];
                  return (
                    <motion.div
                      key={item.id}
                      whileHover={{ y: -5 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden"
                    >
                      <div className="h-48 bg-gradient-to-br from-purple-400 to-teal-400"></div>
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <IconComponent className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg text-gray-900">{item.title}</h3>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColors[item.category]}`}>
                                {item.category}
                              </span>
                            </div>
                          </div>
                          <Star className="w-5 h-5 text-yellow-500 fill-current" />
                        </div>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{item.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {item.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{new Date(item.date).toLocaleDateString()}</span>
                          <div className="flex gap-2">
                            <button className="p-1 hover:bg-gray-100 rounded">
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded">
                              <Share2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm p-6 mb-6"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search portfolio items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Portfolio Items */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-4'}
          >
            {filteredItems.map((item, index) => {
              const IconComponent = categoryIcons[item.category];
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  <div className={`${viewMode === 'list' ? 'w-48' : 'h-32'} bg-gradient-to-br from-purple-400 to-teal-400`}></div>
                  <div className="p-6 flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <IconComponent className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{item.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[item.category]}`}>
                            {item.category}
                          </span>
                        </div>
                      </div>
                      {item.featured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                    </div>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{new Date(item.date).toLocaleDateString()}</span>
                      <div className="flex gap-1">
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <Share2 className="w-3 h-3" />
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <Download className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </>
      )}

      {activeTab === 'achievements' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {mockAchievements.map((achievement, index) => (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 rounded-full ${levelColors[achievement.level]}`}>
                  {achievement.icon === 'trophy' && <Trophy className="w-6 h-6" />}
                  {achievement.icon === 'star' && <Star className="w-6 h-6" />}
                  {achievement.icon === 'heart' && <Heart className="w-6 h-6" />}
                  {achievement.icon === 'book-open' && <BookOpen className="w-6 h-6" />}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{achievement.title}</h3>
                  <p className="text-sm text-gray-500">{achievement.category}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-3">{achievement.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {new Date(achievement.date).toLocaleDateString()}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${levelColors[achievement.level]} bg-opacity-10`}>
                  {achievement.level}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {activeTab === 'skills' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {skillsData.map((skillCategory, index) => (
            <motion.div
              key={skillCategory.category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{skillCategory.category}</h3>
                <span className="text-sm text-gray-500">{skillCategory.level}% Proficiency</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${skillCategory.level}%` }}
                  transition={{ delay: index * 0.1 + 0.5, duration: 1 }}
                  className="bg-gradient-to-r from-purple-500 to-teal-500 h-2 rounded-full"
                ></motion.div>
              </div>
              <div className="flex flex-wrap gap-2">
                {skillCategory.skills.map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};